import { useSelector } from 'react-redux';
import { OBHardwareType } from '../../../types';
import {
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { featureFlagSelector } from '../../../reducers/session';
import onboardingUtils from '../../../reducers/onboarding/utils';
import * as React from 'react';
import {
  memoizedHardwareAllotmentSelectors,
  memoizedHardwareSelectors,
} from '../../../selectors/hardwareSelectors';
import WarningAlert from '../../../widgets/WarningAlert';
import { getSeriesLabel } from './v2/sensor/constants';
import { withTrashableContext } from '../../../hooks';
import { Tooltip } from '@rtkwlf/ui';
import { StandardTooltipOverlay } from '../../../constants';
import Icon from '../../../widgets/Icons';

type Props = {
  type: OBHardwareType;
};

const VirtualLogCollectorInfo = () => {
  const tooltip =
    'The Virtual Log Collector (vLC) is a virtual appliance that collects security relevant syslogs for analysis. You can download the vLC as an OVA package and deploy it onto a VMware ESXi hypervisor.';

  return (
    <div>
      Sites without sensors can collect essential logs using the Virtual Log
      Collector.
      <Tooltip
        className="ml-2"
        placement={'right'}
        overlay={<StandardTooltipOverlay>{tooltip}</StandardTooltipOverlay>}
      >
        <Icon.Question />
      </Tooltip>
    </div>
  );
};

export const RawHardwareAllotmentInfo = (props: Props) => {
  const { type } = props;
  const modelAllotmentSelector = React.useMemo(
    () => memoizedHardwareAllotmentSelectors[type],
    [type],
  );
  const modelAllotment = useSelector(modelAllotmentSelector);
  const seriesAllotment = useSelector(
    memoizedHardwareSelectors.getPurchasedGenericSeries,
  );
  const remainingSeriesAllotment = useSelector(
    memoizedHardwareSelectors.getRemainingGenericSeries,
  );
  const genericSeriesEnabled = useSelector(
    onboardingSelector.showGenericSeries,
  );

  const availableHardware = useSelector(
    memoizedOnboardingSelectors.getAvailableHardware,
  )[type];

  const availableSeries = useSelector(
    onboardingSelector.getAvailableGenericSensors,
  );

  const virtualSensorsEnabled = useSelector(
    featureFlagSelector.onboarding.virtualSensors,
  );

  const incompleteHardwareSelector = React.useMemo(
    () => memoizedHardwareSelectors.getIncompleteHardware(type),
    [type],
  );
  const incompleteHardware = useSelector(incompleteHardwareSelector);

  const modelAllotmentInfo = React.useMemo(() => {
    return Object.entries(modelAllotment)
      .filter(([, quantity]) => quantity > 0)
      .map(([model, quantity]) => {
        const remainingCount = incompleteHardware[model] ?? 0;
        const showRemainingCount = remainingCount < modelAllotment[model];
        return (
          <li key={model}>
            <span>
              {quantity} x{' '}
              {onboardingUtils.getHardwareInfoByHardwareKey(
                availableHardware,
                model,
              )?.label ?? model}
            </span>
            {showRemainingCount && (
              <span style={{ color: '#707F86', paddingLeft: '10px' }}>
                ({remainingCount} remaining)
              </span>
            )}
          </li>
        );
      });
  }, [modelAllotment, availableHardware, incompleteHardware]);

  const seriesAllotmentInfo = React.useMemo(() => {
    return Object.entries(seriesAllotment)
      .filter(([, quantity]) => quantity > 0)
      .map(([series, quantity]) => {
        const remainingCount = remainingSeriesAllotment[series] ?? 0;
        const showRemainingCount = remainingCount < seriesAllotment[series];
        return (
          <li key={series}>
            <span>
              {quantity} x {getSeriesLabel(availableSeries, series)}
            </span>
            {showRemainingCount && (
              <span style={{ color: '#707F86', paddingLeft: '10px' }}>
                ({remainingCount} remaining)
              </span>
            )}
          </li>
        );
      });
  }, [seriesAllotment, availableSeries, remainingSeriesAllotment]);

  return (
    <div className="m-3 onboarding-allotment-info">
      {type === OBHardwareType.SENSOR ? (
        <>
          <p>
            Configure all sensors to be provisioned and shipped. Sensors perform
            network detection and on-premise log collection.
          </p>
          {virtualSensorsEnabled && <VirtualLogCollectorInfo />}
        </>
      ) : (
        <p>
          Configure all physical scanners to be provisioned and shipped.
          Scanners perform internal vulnerability scans of network assets.
        </p>
      )}
      <p>These {type} are included with your current subscription:</p>
      <ul>
        {genericSeriesEnabled && type === OBHardwareType.SENSOR
          ? seriesAllotmentInfo
          : modelAllotmentInfo}
      </ul>
      {type === OBHardwareType.SENSOR && (
        <WarningAlert>
          Arctic Wolf appliances require that you must define an outbound
          security rule for communication from the appliance's management IP
          address to the addresses listed on our{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://dashboard.arcticwolf.com/resource-center/allow-list"
          >
            Allowlist Requirements page
          </a>
          .
        </WarningAlert>
      )}
    </div>
  );
};

export const HardwareAllotmentInfo = withTrashableContext(
  RawHardwareAllotmentInfo,
);
